'use client';

import React, { useMemo, useState } from 'react';
import {
	BigNum,
	LAMPORTS_EXP,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
} from '@velocity-exchange/sdk';
import {
	MAIN_POOL_ID,
	UIMarket,
	USDT_SPOT_MARKET_INDEX,
} from '@velocity-exchange/common';
import { Swap } from '@velocity-exchange/icons';

import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import Button from 'src/components/primitives/Button/Button';
import Divider from 'src/components/primitives/Divider/Divider';
import Input from 'src/components/primitives/Input/Input';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { InputQuickActions } from 'src/components/Inputs/InputQuickActions';
import useModal from 'src/components/Modals/hooks/useModal';
import useSPLTokenBalance from 'src/hooks/useSPLTokenBalance';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountsStore, {
	AccountData,
} from 'src/stores/useVelocityAccountsStore';
import useOraclePrice from 'src/hooks/useOraclePrice';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import { useAccountCreationCost } from 'src/hooks/useAccountCreationCost';
import { useCollateralActionGate } from 'src/components/Modals/CollateralModal/hooks/useCollateralActionGate';
import { useDepositDestinationLimits } from 'src/components/Modals/CollateralModal/hooks/useDepositDestinationLimits';
import { EarnApy } from 'src/components/Modals/CollateralModal/components/EarnApy';
import { BalanceChanges } from 'src/components/Modals/CollateralModal/components/BalanceChanges';
import { RentDisclaimers } from 'src/components/Modals/CollateralModal/components/RentDisclaimers';
import BankruptAccountWarning from 'src/components/Modals/CollateralModal/BankruptAccountWarning';
import { useSwapAndDeposit } from 'src/hooks/velocityActions/swaps/useSwapAndDeposit';
import { useUsdcToUsdtQuote } from './useUsdcToUsdtQuote';
import { isValidQuote } from 'src/utils/swap';
import { getMinOutBigNum, isQuoteForAmount } from 'src/utils/swapAndDeposit';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';
import {
	USDC_ICON_SRC,
	USDC_MAINNET_MINT,
	USDC_PRECISION_EXP,
} from 'src/constants/swapAndDeposit';
import { MIN_LEFTOVER_SOL, ZERO_QUOTE_BIGNUM } from 'src/constants/constants';
import Env from 'src/environmentVariables/EnvironmentVariables';

export interface SwapAndDepositModalProps {
	targetAccountKey?: string | null;
	collateralPoolId?: number | null;
}

/** One "bigInput"-style row of the fixed From/To pair. */
const TokenAmountRow = ({
	label,
	symbol,
	value,
	onChangeValue,
	elementRight,
}: {
	label: string;
	symbol: 'USDC' | 'USDT';
	value: string;
	/** Omit to render a read-only row (the "To" side of a fixed pair). */
	onChangeValue?: (val: string) => void;
	elementRight?: React.ReactNode;
}) => (
	<div className="flex w-full flex-col gap-sm rounded-sm border border-input-border bg-input-bg p-md">
		<div className="flex items-center justify-between">
			<span className="typo-body-xs text-text-secondary">{label}</span>
			{elementRight}
		</div>
		<div className="flex items-center justify-between gap-sm">
			<div className="flex shrink-0 items-center gap-xs">
				{symbol === 'USDC' ? (
					// USDC isn't a Velocity spot market, so it has no CDN icon; use the vendored local one instead
					<img src={USDC_ICON_SRC} alt="USDC icon" className="h-4 w-4" />
				) : (
					<MarketIcon marketSymbol={symbol} sizeClass="w-4 h-4" />
				)}
				<span className="typo-label-md text-text-default">{symbol}</span>
			</div>
			{onChangeValue ? (
				<Input
					variant="headless"
					type="number"
					placeholder="0.00"
					value={value}
					onChange={onChangeValue}
					inputClassName="w-full max-w-full text-right bg-transparent typo-label-md placeholder:text-text-secondary text-text-default"
				/>
			) : (
				<span className="typo-label-md text-text-default">
					{value || <span className="text-text-secondary">0.00</span>}
				</span>
			)}
		</div>
	</div>
);

const SwapAndDepositForm = ({
	targetAccountKey,
	collateralPoolId,
	usdtMarket,
}: SwapAndDepositModalProps & { usdtMarket: SpotMarketConfig }) => {
	const { hide, show } = useModal();
	const isWalletConnected = useWalletIsConnected();
	const solBalance = useVelocityStore((s) => s.wallet.currentSolBalance);
	const userStatsAccount = useVelocityStore((s) => s.userStatsAccount);

	const usdtUiMarket = UIMarket.createSpotMarket(USDT_SPOT_MARKET_INDEX);

	const poolId = collateralPoolId ?? MAIN_POOL_ID;
	const accountKey = targetAccountKey ?? '';
	const selectedTargetAccount = useVelocityAccountsStore(
		(s) => s.accounts[accountKey]
	);
	const accountExists = !!selectedTargetAccount;

	const [usdcAmount, setUsdcAmount] = useState('');
	const [usdcBalanceNum, isUsdcBalanceLoading] = useSPLTokenBalance(
		USDC_MAINNET_MINT,
		USDC_PRECISION_EXP.toNumber()
	);
	const usdcBalance = BigNum.fromPrint(
		String(usdcBalanceNum ?? 0),
		USDC_PRECISION_EXP
	);

	const usdcAmountBigNum = useMemo(
		() => parseInputBigNumOrZero(usdcAmount || '0', USDC_PRECISION_EXP),
		[usdcAmount]
	);

	const { quote, quoteClient, isQuoteLoading } = useUsdcToUsdtQuote({
		usdcAmount,
		usdtMint: usdtMarket.mint,
		enabled: isWalletConnected,
	});

	const isQuoteForCurrentAmount = useMemo(
		() => isQuoteForAmount(quote, usdcAmountBigNum),
		[quote, usdcAmountBigNum]
	);

	const displayQuote = isQuoteForCurrentAmount ? quote : null;

	const quotedOutBigNum = displayQuote
		? BigNum.from(displayQuote.outAmount, usdtMarket.precisionExp)
		: BigNum.zero(QUOTE_PRECISION_EXP);

	const rate =
		displayQuote && usdcAmountBigNum.gtZero()
			? quotedOutBigNum.toNum() / usdcAmountBigNum.toNum()
			: null;

	const oraclePrice =
		useOraclePrice(usdtUiMarket.marketId) ?? ZERO_QUOTE_BIGNUM;

	// SOL-for-gas / account-creation parity with DepositModal.
	const { totalCost: totalAccountCreationCost } = useAccountCreationCost();
	const solRequiredToDeposit = (
		accountExists ? BigNum.zero(LAMPORTS_EXP) : totalAccountCreationCost
	).add(MIN_LEFTOVER_SOL);
	const hasEnoughSolInWallet = solBalance.gte(solRequiredToDeposit);

	// The deposit leg lands USDT in the destination account, so it has to clear
	// the same destination rules DepositModal enforces — the swap runs first and
	// can't be undone if the deposit was never going to be allowed.
	const { maxDepositAmount, isAccountBankrupt, isEmptyAmplifyAccount } =
		useDepositDestinationLimits({
			market: usdtMarket,
			targetAccountKey: accountKey,
			targetAccount: selectedTargetAccount as AccountData | undefined,
		});

	// New users must clear the minimum initial deposit (USDT ≈ $1).
	const minOutForGate = displayQuote
		? getMinOutBigNum(displayQuote, usdtMarket.precisionExp)
		: BigNum.zero(QUOTE_PRECISION_EXP);
	const minInitialDeposit = userStatsAccount
		? undefined
		: BigNum.fromPrint(
				String(Env.minInitialDepositValue),
				usdtMarket.precisionExp
			);
	const isBelowMinDeposit = !!minInitialDeposit?.gt(minOutForGate);

	const { swapAndDeposit, isSubmitting } = useSwapAndDeposit();

	const { canSubmit } = useCollateralActionGate({
		amount: usdcAmountBigNum,
		amountInput: usdcAmount,
		maxAmount: usdcBalance,
		maxAmountStatus: isUsdcBalanceLoading ? 'loading' : 'loaded',
		isSubmitting,
		isWalletConnected,
		extraChecks: [
			{ id: 'QUOTE_STALE', blocked: !!quote && !isQuoteForCurrentAmount },
			{
				id: 'QUOTE_MISSING',
				blocked: !isValidQuote(displayQuote) || !quoteClient,
			},
			{ id: 'QUOTE_LOADING', blocked: isQuoteLoading },
			{ id: 'INSUFFICIENT_SOL', blocked: !hasEnoughSolInWallet },
			{ id: 'BELOW_MIN_DEPOSIT', blocked: isBelowMinDeposit },
			{ id: 'EMPTY_AMPLIFY_ACCOUNT', blocked: isEmptyAmplifyAccount },
			{
				// Compared against the USDT that will actually be deposited, not the
				// USDC being spent.
				id: 'EXCEEDS_DESTINATION_MAX',
				blocked: !!maxDepositAmount && minOutForGate.gt(maxDepositAmount),
			},
		],
	});

	const handleSubmit = async () => {
		if (!displayQuote || !quoteClient) return;

		const result = await swapAndDeposit({
			quote: displayQuote,
			quoteClient,
			usdtMarket,
			targetAccountKey: accountKey,
			poolId,
			// Execution may widen slippage past the rung this modal quoted at, which
			// lowers the deposited amount — the minimum has to hold there too.
			minAcceptableOut: minInitialDeposit,
			// Close the instant account creation succeeds, before the
			// account-subscription settling sleeps. `hide()` below stays as a
			// safety net for every other path.
			onAccountCreated: hide,
		});

		if (result === 'deposited') {
			hide();
		} else if (result === 'swap_only') {
			// The hook prefilled the deposit store; hand the user a deposit
			// modal already pointed at USDT with the received amount.
			show({
				type: 'deposit',
				props: {
					collateralIndex: USDT_SPOT_MARKET_INDEX,
					collateralPoolId: poolId,
					targetAccountKey: accountKey || null,
					defaultLargestBalance: false,
				},
			});
		}
		// 'failed': stay put; the error toast already explained.
	};

	if (isAccountBankrupt) {
		return (
			<BaseModal.Container
				onClose={hide}
				dataTestId="swap-and-deposit-bankrupt-warning"
			>
				<BaseModal.Title
					onClose={hide}
					closeButtonTestId="swap-and-deposit-bankrupt-warning-close"
				></BaseModal.Title>
				<BaseModal.Body>
					<BankruptAccountWarning />
				</BaseModal.Body>
			</BaseModal.Container>
		);
	}

	return (
		<BaseModal.Container onClose={hide} dataTestId="swap-and-deposit-modal">
			<BaseModal.Title
				onClose={hide}
				closeButtonTestId="swap-and-deposit-modal-close"
			>
				Swap &amp; Deposit
			</BaseModal.Title>

			<BaseModal.Body>
				<div className="relative flex flex-col gap-sm">
					<TokenAmountRow
						label="From"
						symbol="USDC"
						value={usdcAmount}
						onChangeValue={setUsdcAmount}
						elementRight={
							<InputQuickActions.SizePresets
								onClick={(amount) => setUsdcAmount(amount.print())}
								maxAmount={usdcBalance}
								isLoading={isUsdcBalanceLoading}
							/>
						}
					/>
					<IconButton
						size="md"
						className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-container-bg text-text-secondary [&_svg]:rotate-90"
						variant="primary"
						icon={Swap}
						disabled
						dataTestId="swap-and-deposit-fixed-pair-icon"
					/>
					<TokenAmountRow
						label="To"
						symbol="USDT"
						value={displayQuote ? quotedOutBigNum.print() : ''}
					/>
				</div>

				<div className="flex items-center justify-between">
					<span className="typo-body-xs text-text-secondary">Rate</span>
					<span className="typo-body-xs text-text-default">
						{rate
							? `1 USDC ≈ ${rate.toLocaleString(Env.locale, {
									maximumFractionDigits: 5,
									minimumFractionDigits: 5,
								})} USDT`
							: '-'}
					</span>
				</div>

				<EarnApy
					selectedMarketIndex={USDT_SPOT_MARKET_INDEX}
					marketDisplaySymbol={usdtUiMarket.uiSymbols.marketDisplaySymbol}
				/>

				<Divider className="bg-stroke-divider" />

				<BalanceChanges
					selectedCollatBank={usdtMarket}
					targetAccountKey={accountKey}
					selectedTargetAccount={selectedTargetAccount as AccountData}
					collatTransferAmountBigNum={quotedOutBigNum}
					assetCurrentPrice={oraclePrice}
					baseAssetDisplaySymbol={usdtUiMarket.uiSymbols.baseAssetDisplaySymbol}
				/>
			</BaseModal.Body>

			<BaseModal.Footer className="flex-col items-stretch">
				<Button
					className="w-full"
					size="lg"
					onClick={handleSubmit}
					disabled={!canSubmit}
					isLoading={isSubmitting}
					dataTestId="swap-and-deposit-submit"
				>
					{isBelowMinDeposit
						? `Minimum Initial Deposit: $${Env.minInitialDepositValue}`
						: 'Swap & Deposit'}
				</Button>
				<RentDisclaimers
					accountExists={accountExists}
					totalAccountCreationCost={totalAccountCreationCost}
					hasEnoughSolInWallet={hasEnoughSolInWallet}
					solRequiredToDeposit={solRequiredToDeposit}
					isLoadingWalletBalance={isUsdcBalanceLoading}
				/>
			</BaseModal.Footer>
		</BaseModal.Container>
	);
};

/**
 * Resolves the fixed USDT destination before rendering the form, so everything
 * below can treat the market as present instead of threading `undefined`
 * through every derivation and hook.
 */
const SwapAndDepositModal = (props: SwapAndDepositModalProps) => {
	const spotMarkets = useSpotMarkets();
	const usdtMarket = spotMarkets.find(
		(m) => m.marketIndex === USDT_SPOT_MARKET_INDEX
	);

	if (!usdtMarket) return null;

	return <SwapAndDepositForm {...props} usdtMarket={usdtMarket} />;
};

export default React.memo(SwapAndDepositModal);
